const STORAGE_KEY = 'postLoginRedirect';

// An abandoned login must not hijack a much later navigation, so stored targets expire.
const MAX_AGE_MS = 10 * 60 * 1000; // 10 minutes

const OFF_ORIGIN_PREFIXES = ['//', '/\\'];

const NON_RESTORABLE_PATHS = ['/', '/dashboard'];
const ORY_PATH_PREFIX = '/ui/';

interface StoredLoginRedirect {
	path: string;
	storedAt: number;
}

const isOffOrigin = (path: string): boolean =>
	OFF_ORIGIN_PREFIXES.some((prefix) => path.startsWith(prefix));

const getValidLoginRedirect = (url?: string): string | undefined => {
	if (!url || !url.startsWith('/') || isOffOrigin(url)) {
		return undefined;
	}

	try {
		const resolvedUrl = new URL(url, window.location.origin);
		if (resolvedUrl.origin !== window.location.origin) {
			return undefined;
		}

		if (
			NON_RESTORABLE_PATHS.includes(resolvedUrl.pathname) ||
			resolvedUrl.pathname.startsWith(ORY_PATH_PREFIX)
		) {
			return undefined;
		}

		return `${resolvedUrl.pathname}${resolvedUrl.search}${resolvedUrl.hash}`;
	} catch {
		return undefined;
	}
};

const parseStoredLoginRedirect = (raw: string | null): string | undefined => {
	if (!raw) {
		return undefined;
	}

	let stored: StoredLoginRedirect;
	try {
		stored = JSON.parse(raw) as StoredLoginRedirect;
	} catch {
		return undefined;
	}

	if (typeof stored?.path !== 'string' || typeof stored?.storedAt !== 'number') {
		return undefined;
	}

	const age = Date.now() - stored.storedAt;
	if (age < 0 || age > MAX_AGE_MS) {
		return undefined;
	}

	return getValidLoginRedirect(stored.path);
};

export const rememberLoginRedirect = (
	url = window.location.pathname + window.location.search + window.location.hash,
): void => {
	const redirect = getValidLoginRedirect(url);
	if (!redirect) {
		return;
	}

	const stored: StoredLoginRedirect = { path: redirect, storedAt: Date.now() };

	try {
		window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(stored));
	} catch {
		return;
	}
};

export const consumeLoginRedirect = (): string | undefined => {
	let raw: string | null = null;

	try {
		raw = window.sessionStorage.getItem(STORAGE_KEY);
	} catch {
		// Storage access can fail in restricted browser contexts.
	}

	try {
		window.sessionStorage.removeItem(STORAGE_KEY);
	} catch {
		return undefined;
	}

	return parseStoredLoginRedirect(raw);
};

export const buildStepUpUrl = (redirectTo: string): string => {
	try {
		const stepUpUrl = new URL(redirectTo);
		stepUpUrl.searchParams.set('return_to', window.location.href);
		return stepUpUrl.href;
	} catch {
		return redirectTo;
	}
};
